/**
 * Category / section colors for vehicle build surfaces (web).
 * Keep in sync with packages/design-tokens/src/sectionColors.ts (mobile uses the package).
 * CRA cannot transpile workspace TS packages, so web keeps a local copy.
 *
 * Palette spans the accent set (orange, red, amber, emerald, sky, violet, rose, slate)
 * plus extra hues so adjacent categories do not collapse into the same warm family.
 */

/** Accent palette hex (matches build profile accents). */
export const ACCENT_COLOR_HEX = {
  orange: '#F97316',
  red: '#EF4444',
  amber: '#F59E0B',
  emerald: '#10B981',
  sky: '#0EA5E9',
  violet: '#8B5CF6',
  rose: '#F43F5E',
  slate: '#94A3B8',
} as const;

export const CATEGORY_COLOR_MAP = {
  // Core modification categories — one distinct hue each
  Engine: ACCENT_COLOR_HEX.orange,
  Turbo: ACCENT_COLOR_HEX.violet,
  'Forced Induction': ACCENT_COLOR_HEX.violet,
  Intake: '#38BDF8',
  Drivetrain: ACCENT_COLOR_HEX.sky,
  Transmission: ACCENT_COLOR_HEX.sky,
  'Fuel System': ACCENT_COLOR_HEX.amber,
  Fuel: ACCENT_COLOR_HEX.amber,
  Cooling: '#22D3EE',
  Suspension: ACCENT_COLOR_HEX.emerald,
  Brakes: ACCENT_COLOR_HEX.red,
  Wheels: ACCENT_COLOR_HEX.slate,
  'Wheels and Tires': ACCENT_COLOR_HEX.slate,
  'Wheels/Tires': ACCENT_COLOR_HEX.slate,
  Exhaust: '#6366F1',
  Interior: '#D946EF',
  Exterior: '#84CC16',
  'Body/Aero': '#84CC16',
  Electronics: '#14B8A6',
  'ECU/Tuning': '#14B8A6',
  Ignition: '#14B8A6',
  Safety: ACCENT_COLOR_HEX.rose,
  Chassis: '#A78BFA',
  General: '#6B7280',
  Miscellaneous: '#6B7280',
  Other: '#6B7280',

  // Page / build section aliases
  Performance: '#EAB308',
  Media: '#EC4899',
  Events: '#38BDF8',
  Passport: '#34D399',
  Recognition: '#FB7185',
  Contributors: '#818CF8',
  Shops: '#2DD4BF',
} as const;

export type CategoryColorName = keyof typeof CATEGORY_COLOR_MAP;

export const SECTION_FALLBACK_COLOR = '#6B7280';

export function normalizeSectionKey(name: string): string {
  return name.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

const LOOKUP: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_COLOR_MAP).map(([k, v]) => [normalizeSectionKey(k), v])
);

export function resolveSectionColor(name: string | null | undefined): string {
  if (!name) return SECTION_FALLBACK_COLOR;
  return LOOKUP[normalizeSectionKey(name)] || SECTION_FALLBACK_COLOR;
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.replace('#', '').trim();
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return Number.isFinite(r) ? { r, g, b } : null;
  }
  if (raw.length === 6) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return Number.isFinite(r) ? { r, g, b } : null;
  }
  return null;
}

export function withAlpha(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}

export interface SectionSurfaceTokens {
  color: string;
  border: string;
  borderHover: string;
  borderActive: string;
  tint: string;
  tintHover: string;
  text: string;
  focusRing: string;
}

export function sectionSurfaceTokens(hex: string): SectionSurfaceTokens {
  const color = hex || SECTION_FALLBACK_COLOR;
  return {
    color,
    border: withAlpha(color, 0.32),
    borderHover: withAlpha(color, 0.55),
    borderActive: withAlpha(color, 0.72),
    tint: withAlpha(color, 0.07),
    tintHover: withAlpha(color, 0.12),
    text: color,
    focusRing: withAlpha(color, 0.45),
  };
}

export function sectionSurfaceCssVars(hex: string): Record<string, string> {
  const t = sectionSurfaceTokens(hex);
  return {
    '--section-color': t.color,
    '--section-border': t.border,
    '--section-border-hover': t.borderHover,
    '--section-border-active': t.borderActive,
    '--section-tint': t.tint,
    '--section-tint-hover': t.tintHover,
    '--section-text': t.text,
    '--section-focus': t.focusRing,
  };
}
